<template>
  <div v-if="showModal" class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ $t('words.statistics') }}</h3>
        <button class="close-button" @click="closeModal">×</button>
      </div>

      <div class="modal-body">
        <p class="statistics-description">{{ $t('words.statisticsRequired') }}</p>

        <VeeForm @submit="saveStatistics">
          <div class="wdgt-form">
            <div class="form-field">
              <label for="wdgt-stats-country">{{ $t('words.statsCountryCode') }}*</label>
              <div class="country-selector">
                <input
                  id="wdgt-stats-country"
                  type="text"
                  :placeholder="$t('words.searchCountry')"
                  @focus="showCountryDropdown = true"
                  @blur="hideCountryDropdown"
                  :value="selectedCountryName"
                  readonly
                />
                <div v-if="showCountryDropdown" class="country-dropdown">
                  <input
                    type="text"
                    v-model="countrySearch"
                    :placeholder="$t('words.searchCountry')"
                    class="country-search"
                    @focus="keepCountryDropdownOpen"
                    @blur="hideCountryDropdownDelayed"
                    @click.stop
                    ref="countrySearchInput"
                  />
                  <div class="country-list">
                    <div
                      v-for="country in filteredCountries"
                      :key="country.code"
                      @click="selectCountry(country)"
                      class="country-option"
                    >
                      {{ country.name }} ({{ country.code }})
                    </div>
                  </div>
                </div>
              </div>
              <VeeField
                name="statsCountryCode"
                type="hidden"
                :modelValue="statisticsData.statsCountryCode"
                @update:modelValue="statisticsData.statsCountryCode = $event"
                rules="required"
              />
              <ErrorMessage name="statsCountryCode" class="error" />
            </div>

            <div class="form-field">
              <label for="wdgt-stats-postal">{{ $t('words.statsPostalCode') }}*</label>
              <VeeField
                id="wdgt-stats-postal"
                :modelValue="statisticsData.statsPostalCode"
                @update:modelValue="statisticsData.statsPostalCode = $event"
                name="statsPostalCode"
                type="text"
                :placeholder="$t('words.statsPostalCode')"
                rules="required"
              />
              <ErrorMessage name="statsPostalCode" class="error" />
            </div>

            <div class="form-field">
              <label for="wdgt-stats-how-met">{{ $t('words.statsHowMet') }}*</label>
              <div class="how-met-selector">
                <input
                  id="wdgt-stats-how-met"
                  type="text"
                  :placeholder="$t('words.statsHowMet')"
                  @focus="showHowMetDropdown = true"
                  @blur="hideHowMetDropdown"
                  :value="selectedHowMetName"
                  readonly
                />
                <div v-if="showHowMetDropdown" class="how-met-dropdown">
                  <div class="how-met-list">
                    <div
                      v-for="option in howMetOptions"
                      :key="option.value"
                      @click="selectHowMet(option.value, option.text)"
                      class="how-met-option"
                    >
                      {{ option.text }}
                    </div>
                  </div>
                </div>
              </div>
              <VeeField
                name="statsHowMet"
                type="hidden"
                :modelValue="statisticsData.statsHowMet"
                @update:modelValue="statisticsData.statsHowMet = $event"
                rules="required"
              />
              <ErrorMessage name="statsHowMet" class="error" />
            </div>

            <div v-if="statisticsData.statsHowMet === 'other'" class="form-field">
              <label for="wdgt-stats-how-met-other">{{ $t('words.statsHowMetOther') }}*</label>
              <VeeField
                id="wdgt-stats-how-met-other"
                :modelValue="statisticsData.statsHowMetOther"
                @update:modelValue="statisticsData.statsHowMetOther = $event"
                name="statsHowMetOther"
                as="textarea"
                :placeholder="$t('words.statsHowMetOther')"
                :rules="statisticsData.statsHowMet === 'other' ? 'required' : ''"
                rows="3"
              />
              <ErrorMessage name="statsHowMetOther" class="error" />
            </div>
          </div>

          <div class="checkout-buttons">
            <button type="button" class="checkout d-flex" @click="closeModal">
              {{ $t('actions.cancel') }}
            </button>
            <button type="submit" class="registerButton green d-flex ml-auto" :disabled="isSubmitting">
              {{ $t('actions.save') }}
            </button>
          </div>
        </VeeForm>
      </div>
    </div>
  </div>
</template>

<script>
import { defineRule, Form, Field, ErrorMessage, configure } from "vee-validate";
import { required } from "@vee-validate/rules";
import { i18n } from "@/lang";
import countries from "@/utils/countries";
import CustomerRepository from "@/api/CustomerRepository";
import { Customer } from "@/models/Customer";
import { mapGetters, mapActions } from 'vuex';
import { getCountryFromTimezone } from "@/utils/countryDetection";

defineRule('required', required);

configure({
  generateMessage: (ctx) => {
    const messages = {
      required: i18n.global.t('forms.required'),
    };
    return messages[ctx.rule.name] || `The field ${ctx.field} is invalid`;
  },
});

export default {
  name: 'StatisticsModal',
  components: {
    VeeForm: Form,
    VeeField: Field,
    ErrorMessage
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      statisticsData: {
        statsCountryCode: '',
        statsPostalCode: '',
        statsHowMet: '',
        statsHowMetOther: ''
      },
      countries: countries,
      countrySearch: '',
      showCountryDropdown: false,
      selectedCountryName: '',
      showHowMetDropdown: false,
      selectedHowMetName: '',
      isSubmitting: false,
      howMetOptions: [
        { value: 'search_engines', text:  this.$t('words.howMetOptions.search_engines')  },
        { value: 'social_media', text: this.$t('words.howMetOptions.social_media') },
        { value: 'artificial_intelligence', text:  this.$t('words.howMetOptions.artificial_intelligence') },
        { value: 'friends_or_family', text: this.$t('words.howMetOptions.friends_or_family') },
        { value: 'advertising', text: this.$t('words.howMetOptions.advertising') },
        { value: 'email', text: this.$t('words.howMetOptions.email') },
        { value: 'other', text: this.$t('words.howMetOptions.other') }
      ]
    }
  },
  computed: {
    ...mapGetters('session', ['customer']),
    filteredCountries() {
      if (!this.countrySearch) {
        return this.countries;
      }
      return this.countries.filter(country =>
        country.name.toLowerCase().includes(this.countrySearch.toLowerCase()) ||
        country.code.toLowerCase().includes(this.countrySearch.toLowerCase())
      );
    }
  },
  methods: {
    ...mapActions('session', ['updateCustomerData']),
    closeModal() {
      this.$emit('close');
    },

    selectCountry(country) {
      this.statisticsData.statsCountryCode = country.code;
      this.selectedCountryName = country.name;
      this.showCountryDropdown = false;
    },

    hideCountryDropdown() {
      setTimeout(() => {
        this.showCountryDropdown = false;
      }, 200);
    },

    keepCountryDropdownOpen() {
      this.showCountryDropdown = true;
    },

    hideCountryDropdownDelayed() {
      setTimeout(() => {
        this.showCountryDropdown = false;
      }, 200);
    },

    selectHowMet(value, text) {
      this.statisticsData.statsHowMet = value;
      this.selectedHowMetName = text;
      this.showHowMetDropdown = false;

      if (value !== 'other') {
        this.statisticsData.statsHowMetOther = '';
      }
    },

    hideHowMetDropdown() {
      setTimeout(() => {
        this.showHowMetDropdown = false;
      }, 200);
    },

    async saveStatistics() {
      this.isSubmitting = true;

      try {
        // Preservar el número de teléfono original para evitar que se corrompa
        const originalPhone = this.customer.contactPhone1;

        // Crear una instancia de Customer correctamente
        const updatedCustomerData = {
          ...this.customer,
          ...this.statisticsData
        };

        // Crear la instancia de Customer
        const customerInstance = new Customer(updatedCustomerData);

        // Restaurar el número de teléfono original después de la construcción
        customerInstance.contactPhone1 = originalPhone;

        // Usar el método correcto del CustomerRepository
        await CustomerRepository.edit(customerInstance);

        // Actualizar el customer en el store de Vuex con los nuevos datos
        await this.updateCustomerData(updatedCustomerData);

        this.$emit('statistics-saved', this.statisticsData);
        this.closeModal();

      } catch (error) {
        console.error('Error saving statistics:', error);
        // Handle error appropriately
      } finally {
        this.isSubmitting = false;
      }
    },

    // Detectar automáticamente el país cuando se abre el modal
    initializeCountryDetection() {
      try {
        // Solo detectar si no hay un país ya seleccionado
        if (!this.statisticsData.statsCountryCode) {
          const detectedCountryCode = getCountryFromTimezone();
          const detectedCountry = this.countries.find(c => c.code === detectedCountryCode);

          if (detectedCountry) {
            this.statisticsData.statsCountryCode = detectedCountry.code;
            this.selectedCountryName = detectedCountry.name;
          }
        }
      } catch (error) {
        console.warn('Error detecting country:', error);
        // Fallback a España si hay error
        const fallbackCountry = this.countries.find(c => c.code === 'ES');
        if (fallbackCountry) {
          this.statisticsData.statsCountryCode = fallbackCountry.code;
          this.selectedCountryName = fallbackCountry.name;
        }
      }
    }
  },

  watch: {
    showModal(newValue) {
      if (newValue) {
        // Cuando se abre el modal, inicializar la detección de país
        this.initializeCountryDetection();
      }
    }
  }
}
</script>

<style scoped>
/* Estilos del modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.close-button:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.statistics-description {
  margin-bottom: 20px;
  color: #666;
}

/* Estilos de formulario consistentes con otros componentes */
.wdgt-form {
  display: flex;
  flex-wrap: wrap;
}

.form-field {
  margin-bottom: 15px;
  width: 50%;
  padding-right: 15px;
}

.form-field > label {
  display: block;
  margin-bottom: 5px;
}

.form-field.checkbox > label {
  display: inline;
  margin-left: 5px;
}

/* Estilos de inputs */
input {
  font-size: 18px;
  padding: 10px;
  margin: 0px 0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid #dedede;
  width: 100%;
}

textarea {
  font-size: 18px;
  padding: 10px;
  margin: 0px 0;
  border: none;
  border-radius: 4px;
  border: 1px solid #dedede;
  width: 100%;
  resize: vertical;
  font-family: inherit;
  min-height: 60px;
}

/* Estilos de botones */
.checkout-buttons {
  display: flex;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid #eee;
}

button.registerButton {
  background-color: #c91919;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

button.checkout {
  background: #acacac;
  color: white;
  font-size: 20px;
  padding: 10px 40px 10px 40px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Estilos de errores */
.form-field .error {
  display: block;
  margin-bottom: 0.5em;
  font-size: .75em;
  color: #f22435;
}

/* Estilos de dropdowns */
.country-selector,
.how-met-selector {
  position: relative;
}

.country-dropdown,
.how-met-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dedede;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.country-search {
  width: 100%;
  padding: 10px;
  border: none;
  border-bottom: 1px solid #eee;
  font-size: 16px;
}

.country-list,
.how-met-list {
  max-height: 150px;
  overflow-y: auto;
}

.country-option,
.how-met-option {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  font-size: 16px;
}

.country-option:hover,
.how-met-option:hover {
  background-color: #f8f9fa;
}

.country-option:last-child,
.how-met-option:last-child {
  border-bottom: none;
}

/* Responsive design */
@media (max-width: 768px) {
  .form-field {
    width: 100%;
  }

  .checkout-buttons {
    flex-direction: column;
    justify-items: center;
    margin-top: 10px;
  }

  .checkout-buttons button {
    width: 100%;
    margin: 0 0 15px 0;
  }
}

@media (max-width: 425px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  input, textarea {
    padding: 8px;
    font-size: 16px;
  }

  .form-field {
    padding-right: 0;
  }
}

/* Estilos para botones cuando están en dispositivos móviles */
@media only screen and (max-width: 768px) {
  .checkout-buttons button {
    width: 100% !important;
    max-width: 100% !important;
    align-items: center;
    justify-content: center;
  }

  .registerButton {
    margin-top: 1rem !important;
  }

  button.checkout {
    margin: 0;
  }
}
</style>
